import { GenericLazyString, gettext } from '@eventbrite/i18n';
import {
    BoltIcon,
    MagicWandIcon,
    MailIcon,
    MegaphoneIcon,
    RefundIcon,
    TicketIcon,
    TrendingIcon,
    UserIcon,
} from '@eventbrite/marmalade';
import React from 'react';
import {
    EB_ADS,
    PAYMENT_AND_SERVICE_FEE,
    SEND_MORE_EMAILS,
} from '../constants/feesStructure';
import { TESTIMONIALS } from '../constants/pricingTestimonials';
import { GrowthEarthSvg } from './SVGs/GrowthEarthSvg';
import { GrowthHandSvg } from './SVGs/GrowthHandSvg';
import { InfinitySvg } from './SVGs/InfinitySvg';
import { MegaphoneSvg } from './SVGs/MegaphoneSvg';
import { TeamworkSvg } from './SVGs/TeamworkSvg';
import { getHost, getLocale } from './utils';

export enum SupportedTLD {
    Canada = 'ca',
    UnitedKingdom = 'uk',
    Ireland = 'ie',
    Australia = 'au',
    NewZealand = 'nz',
    UnitedStates = 'com',
}

type SupportedTLDType = `${SupportedTLD}`;

type PriceOptions = {
    liteTier: string;
    coreTier: string;
    proTier: string;
};

type PlanOptions = {
    flexPlan: PriceOptions;
    proPlan: PriceOptions;
};

type Fees = {
    serviceFee: Fee;
    processingFee?: Fee;
};

type FlexDiscountPriceOptions = {
    coreTier: string;
    proTier: string;
};

type Fee = { percent: string; additionalPerTicketCost?: string };

type DisplayPrices = {
    [key in SupportedTLDType]: PlanOptions;
};

type DisplayFees = {
    [key in SupportedTLDType]: Fees;
};

type DisplayFlexDiscountPrice = {
    [key in SupportedTLD]: FlexDiscountPriceOptions;
};

type getOrganizerFeePerPlanReturnType = Array<string | GenericLazyString>;

const TLD_TO_CURRENCY_CODE = {
    [SupportedTLD.Canada]: 'CAD',
    [SupportedTLD.UnitedKingdom]: 'GBP',
    [SupportedTLD.Ireland]: 'EUR',
    [SupportedTLD.Australia]: 'AUD',
    [SupportedTLD.NewZealand]: 'NZD',
    [SupportedTLD.UnitedStates]: 'USD',
};

const TLD_TO_CURRENCY = {
    [SupportedTLD.Canada]: '$',
    [SupportedTLD.UnitedKingdom]: '£',
    [SupportedTLD.Ireland]: '€',
    [SupportedTLD.Australia]: '$',
    [SupportedTLD.NewZealand]: '$',
    [SupportedTLD.UnitedStates]: '$',
};

const TLD_TO_FEES: DisplayFees = {
    [SupportedTLD.Canada]: {
        serviceFee: { percent: '3.5', additionalPerTicketCost: '1.29' },
        processingFee: { percent: '2.9' },
    },
    [SupportedTLD.UnitedKingdom]: {
        serviceFee: { percent: '6.95', additionalPerTicketCost: '0.59' },
    },
    [SupportedTLD.Ireland]: {
        serviceFee: { percent: '5.9', additionalPerTicketCost: '0.79' },
    },
    [SupportedTLD.Australia]: {
        serviceFee: { percent: '5.35', additionalPerTicketCost: '1.19' },
    },
    [SupportedTLD.NewZealand]: {
        serviceFee: { percent: '4.2', additionalPerTicketCost: '0.55' },
        processingFee: { percent: '2.4' },
    },
    [SupportedTLD.UnitedStates]: {
        serviceFee: { percent: '3.7', additionalPerTicketCost: '1.79' },
        processingFee: { percent: '2.9' },
    },
};

const UNKNOWN_FEES = {
    serviceFee: { percent: '-- %', additionalPerTicketCost: '$ --' },
};

const TLD_TO_PRICES: DisplayPrices = {
    [SupportedTLD.Canada]: {
        flexPlan: { liteTier: '12', coreTier: '33', proTier: '65' },
        proPlan: { liteTier: '39', coreTier: '99', proTier: '199' },
    },
    [SupportedTLD.UnitedKingdom]: {
        flexPlan: { liteTier: '7', coreTier: '19', proTier: '34' },
        proPlan: { liteTier: '19', coreTier: '59', proTier: '99' },
    },
    [SupportedTLD.Ireland]: {
        flexPlan: { liteTier: '9', coreTier: '24', proTier: '49' },
        proPlan: { liteTier: '29', coreTier: '79', proTier: '149' },
    },
    [SupportedTLD.Australia]: {
        flexPlan: { liteTier: '9', coreTier: '24', proTier: '49' },
        proPlan: { liteTier: '29', coreTier: '79', proTier: '159' },
    },
    [SupportedTLD.NewZealand]: {
        flexPlan: { liteTier: '9', coreTier: '24', proTier: '49' },
        proPlan: { liteTier: '29', coreTier: '79', proTier: '159' },
    },
    [SupportedTLD.UnitedStates]: {
        flexPlan: { liteTier: '9', coreTier: '24', proTier: '49' },
        proPlan: { liteTier: '29', coreTier: '79', proTier: '159' },
    },
};

const TLD_FLEX_DISCOUNTED_PRICE: DisplayFlexDiscountPrice = {
    [SupportedTLD.Canada]: { coreTier: '25', proTier: '38' },
    [SupportedTLD.UnitedKingdom]: { coreTier: '15', proTier: '20' },
    [SupportedTLD.Ireland]: { coreTier: '19', proTier: '29' },
    [SupportedTLD.Australia]: { coreTier: '19', proTier: '29' },
    [SupportedTLD.NewZealand]: { coreTier: '19', proTier: '29' },
    [SupportedTLD.UnitedStates]: { coreTier: '19', proTier: '29' },
};

const UNKNOWN_PRICES = {
    flexPlan: {
        liteTier: '$ --',
        coreTier: '$ --',
        proTier: '$ --',
    },
    proPlan: {
        liteTier: '$ --',
        coreTier: '$ --',
        proTier: '$ --',
    },
};

export const PLAN_PERKS = {
    FLEX: [
        {
            icon: <TicketIcon size="xsmall" />,
            text: gettext('Pay as you go (Free up to 25 tickets)'),
        },
        {
            icon: <TrendingIcon size="xsmall" />,
            text: gettext('Essentials for success'),
        },
        {
            icon: <MailIcon size="xsmall" />,
            text: gettext('250 daily email invites'),
        },
        {
            icon: <UserIcon size="xsmall" />,
            text: gettext('Basic support'),
        },
        {
            icon: <MagicWandIcon size="xsmall" />,
            text: gettext('Upgrade to sell more tickets'),
        },
    ],
    PRO: [
        {
            icon: <InfinitySvg />,
            text: gettext('Unlimited events (monthly subscription)'),
        },
        {
            icon: <MegaphoneIcon size="xsmall" />,
            text: gettext('Enhanced support for frequent events'),
        },
        {
            icon: <MailIcon size="xsmall" />,
            text: gettext('Up to 10,000 daily email invites'),
        },
        {
            icon: <UserIcon size="xsmall" />,
            text: gettext('24/7 chat support'),
        },
        {
            icon: <RefundIcon size="xsmall" />,
            text: gettext('Cancel anytime'),
        },
    ],
    PARTNER: [
        {
            icon: <InfinitySvg />,
            text: gettext('Unlimited events (custom pricing)'),
        },
        {
            icon: <MegaphoneIcon size="xsmall" />,
            text: gettext('Advanced support for event businesses'),
        },
        {
            icon: <MailIcon size="xsmall" />,
            text: gettext('Custom daily email invites'),
        },
        {
            icon: <UserIcon size="xsmall" />,
            text: gettext('Priority phone & 24/7 chat support'),
        },
        {
            icon: <BoltIcon size="xsmall" />,
            text: gettext('Partner with a Strategic Advisor'),
        },
    ],
};

export const PRICING_PRO_CTA_BUTTON_EVENT =
    'Organizer - Pricing - Click - Pro CTA';
export const PRICING_FLEX_CTA_BUTTON_EVENT =
    'Organizer - Pricing - Click - Flex CTA';

export const CAPACITY_DROPDOWN_FEATURE = {
    experimentName: 'capacity_dropdown_feature',
    param: 'showCapacityDropdown',
    defaultValue: 'show',
};

export const getTld = () => {
    return getHost().split('.').pop() ?? '';
};

export const isAllowedTld = () => {
    const tld = getTld() as SupportedTLD;
    return Object.values(SupportedTLD).includes(tld);
};

export const getFeesForTld = () => {
    if (isAllowedTld()) {
        const userLocale = getLocale();
        const tld = getTld() as SupportedTLD;
        const allFees = TLD_TO_FEES[tld];
        const localizedFees = Object.keys(allFees).reduce((acc, feeType) => {
            const _feeType = feeType as keyof Fees;
            const currentFee = allFees[_feeType] ?? ({} as Fee);
            return {
                ...acc,
                [_feeType]: Object.keys(currentFee).reduce(
                    (acc, feeCategory) => {
                        let formattedValue;
                        const rawValue = currentFee[feeCategory as keyof Fee];
                        if (!rawValue) return acc;
                        if (feeCategory === 'percent') {
                            formattedValue = new Intl.NumberFormat(userLocale, {
                                style: 'percent',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                            }).format(+rawValue / 100);
                        } else {
                            formattedValue = new Intl.NumberFormat(userLocale, {
                                style: 'currency',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                                currency: TLD_TO_CURRENCY_CODE[tld],
                            }).format(+rawValue);
                        }

                        return {
                            ...acc,
                            [feeCategory]: formattedValue,
                        };
                    },
                    {} as Fees,
                ),
            };
        }, {} as DisplayFees);

        return localizedFees;
    } else {
        return UNKNOWN_FEES;
    }
};

export const getPricesForTLD = () => {
    // protect against anyone using this fn in a non P&P TLD
    if (isAllowedTld()) {
        const userLocale = getLocale();
        const tld = getTld() as SupportedTLD;
        const planOptions = TLD_TO_PRICES[tld];
        const zeroCost = new Intl.NumberFormat(userLocale, {
            style: 'currency',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
            currency: TLD_TO_CURRENCY_CODE[tld],
        }).format(0);

        const localizedPrices = Object.keys(planOptions).reduce((acc, plan) => {
            const _plan = plan as keyof PlanOptions;
            return {
                ...acc,
                [_plan]: Object.keys(planOptions[_plan]).reduce((acc, tier) => {
                    const rawPrice =
                        planOptions[_plan][tier as keyof PriceOptions];
                    const formattedPrice = new Intl.NumberFormat(userLocale, {
                        style: 'currency',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                        currency: TLD_TO_CURRENCY_CODE[tld],
                    }).format(+rawPrice);

                    return {
                        ...acc,
                        [tier]: formattedPrice,
                    };
                }, {} as PlanOptions),
            };
        }, {} as DisplayPrices);

        return { ...localizedPrices, freePlan: { freeTier: zeroCost } };
    } else {
        return UNKNOWN_PRICES;
    }
};

export const getPricesAndCurrencyCodeForTLD = () => {
    if (isAllowedTld()) {
        const tld = getTld() as SupportedTLD;
        const planOptions = TLD_TO_PRICES[tld];
        const currencySymbol = TLD_TO_CURRENCY[tld];
        const zeroCost = `${currencySymbol}${0}`;
        const currencyCode = TLD_TO_CURRENCY_CODE[tld] || null;

        const localizedPrices = Object.keys(planOptions).reduce((acc, plan) => {
            const _plan = plan as keyof PlanOptions;
            return {
                ...acc,
                [_plan]: Object.keys(planOptions[_plan]).reduce((acc, tier) => {
                    const rawPrice =
                        planOptions[_plan][tier as keyof PriceOptions];
                    const formattedPrice = `${currencySymbol}${rawPrice}`;

                    return {
                        ...acc,
                        [tier]: formattedPrice,
                    };
                }, {} as PlanOptions),
            };
        }, {} as DisplayPrices);
        const priceMapping = {
            ...localizedPrices,
            freePlan: { freeTier: zeroCost },
        };

        return { currencyCode, priceMapping };
    } else {
        return UNKNOWN_PRICES;
    }
};

export const getPlanOptions = (priceMapping: any) => {
    return {
        FLEX: [
            {
                label: gettext('Up to 25'),
                icon: <GrowthHandSvg />,
                cost: gettext('Free'),
                discountedCost: null,
                heapId: 'organizer-pricing-all-click-pricing-card-flex-capacity-25',
            },
            {
                label: gettext('Up to 100'),
                icon: <GrowthEarthSvg />,
                cost: priceMapping.flexPlan.liteTier,
                discountedCost: null,
                heapId: 'organizer-pricing-all-click-pricing-card-flex-capacity-100',
            },
            {
                label: gettext('Up to 250'),
                icon: <TeamworkSvg />,
                cost: priceMapping.flexPlan.coreTier,
                discountedCost: null,
                heapId: 'organizer-pricing-all-click-pricing-card-flex-capacity-250',
            },
            {
                label: gettext('Unlimited'),
                icon: <MegaphoneSvg />,
                cost: priceMapping.flexPlan.proTier,
                discountedCost: null,
                heapId: 'organizer-pricing-all-click-pricing-card-flex-capacity-unlimited',
            },
        ],
        PRO: [
            {
                label: gettext('Up to 100'),
                icon: <GrowthEarthSvg />,
                cost: priceMapping.proPlan.liteTier,
                discountedCost: priceMapping.freePlan.freeTier,
                heapId: 'organizer-pricing-all-click-pricing-card-pro-capacity-100',
            },
            {
                label: gettext('Up to 250'),
                icon: <TeamworkSvg />,
                cost: priceMapping.proPlan.coreTier,
                discountedCost: priceMapping.freePlan.freeTier,
                heapId: 'organizer-pricing-all-click-pricing-card-pro-capacity-250',
            },
            {
                label: gettext('Unlimited'),
                icon: <MegaphoneSvg />,
                cost: priceMapping.proPlan.proTier,
                discountedCost: priceMapping.freePlan.freeTier,
                heapId: 'organizer-pricing-all-click-pricing-card-pro-capacity-unlimited',
            },
        ],
    };
};

export const getFlexDiscountedPricesForTLD = () => {
    if (isAllowedTld()) {
        const userLocale = getLocale();
        const tld = getTld() as SupportedTLD;
        const ticketOptions = TLD_FLEX_DISCOUNTED_PRICE[tld] || {};

        const localizedPrices = Object.keys(ticketOptions).reduce(
            (ticketPlan, planCategory) => {
                const rawPrice =
                    ticketOptions[
                        planCategory as keyof FlexDiscountPriceOptions
                    ];
                const formattedPrice = new Intl.NumberFormat(userLocale, {
                    style: 'currency',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                    currency: TLD_TO_CURRENCY_CODE[tld],
                }).format(+rawPrice);

                return {
                    ...ticketPlan,
                    [planCategory]: formattedPrice,
                };
            },
            {} as DisplayFlexDiscountPrice,
        );

        return { ...localizedPrices };
    } else {
        return {};
    }
};

export const getOrganizerFeePerPlan = (): getOrganizerFeePerPlanReturnType => {
    if (isAllowedTld()) {
        const priceMapping = getPricesForTLD() as any as PlanOptions;
        const organizerFee = [];
        const freeTier = gettext('Free');
        const flexTier = gettext('Starting at %(flex)s per event', {
            flex: priceMapping.flexPlan.liteTier,
        });
        const proTier = gettext('Starting at %(pro)s per month', {
            pro: priceMapping.proPlan.liteTier,
        });
        const premiumTier = gettext('None');
        organizerFee.push(freeTier, flexTier, proTier, premiumTier);
        return organizerFee;
    } else {
        return ['$ --', '$ --', '$ --', '$ --'];
    }
};

export const getFeesBreakdownDataByTld = () => {
    const tld = getTld() as SupportedTLD;

    return {
        paymentAndServiceData: PAYMENT_AND_SERVICE_FEE[tld],
        sendMoreEmailsData: SEND_MORE_EMAILS[tld],
        ebAdsCTA: EB_ADS[tld],
    };
};

export const getTestimonialsDataByTld = () => {
    const tld = getTld() as SupportedTLD;
    const DataByTLD = TESTIMONIALS.filter((item: any) =>
        item.testimonialFortlds.includes(tld),
    );
    return DataByTLD;
};

export const shouldShowPricingAndPackagingContent = () => isAllowedTld();
